import { FACE_DIR, type Face } from './faces';

// WHAT SHAPE A PART IS, past its bounding box — the one fact that lets "centred" mean the
// wood rather than the rectangle around it.
//
// A face is a rectangle cut out of the part's box (see partFaces, which argues why), and
// every hole is placed on one. "Centred across the height" then means half the box's height,
// which on a rail whose top is shaped is not half the wood at the depth the bore sits at. The
// two agree on a plank and disagree on everything a reconstruction is good at.
//
// SO: FOR EACH BUCKET ALONG ONE AXIS, WHERE THE MATERIAL STARTS AND STOPS ALONG ANOTHER.
// Three pairs and not six, because the order is fixed: a hole's first stated line resolves
// against the box, and its second resolves against the section at the first (see holePlace).
// The pairs are exactly the (u, v) of the six faces — front and back share one, left and
// right another, top and bottom the third.
//
// THE ENVELOPE AND NOT THE SOLID. A section through a C-shaped part is material, air,
// material; what is recorded is the first material to the last, so "centred" is halfway
// between the two faces you can see. It can therefore land in a gap, and that is the answer
// rather than a bug: the alternative — the midpoint of the widest solid run — jumps between
// the two arms of a symmetric C as the section moves, which is a hole that walks.
//
// IN FRACTIONS AND NOT MILLIMETRES, so the record survives a rescale. A reconstruction is
// unitless until a printed dimension anchors it, and the same mesh is re-anchored whenever
// its part is re-measured; a profile in millimetres would be a measurement of a size the part
// no longer has. Fractions are of the box's own extent along each axis, 0 at its minimum.
export type Axis = 'x' | 'y' | 'z';

// Where the material begins and ends in this bucket, or null for a bucket with none in it —
// which happens at the ends of a rounded part, and on any bucket a triangle simply misses.
export type Band = [number, number] | null;

// Keyed by the two axes, bucketing one first: 'xz' is the height's extent at each width.
export type PartProfile = { buckets: number; bands: Record<string, Band[]> };

// NO `three` IN THIS FILE, and that is the point of it being two files. holePlace reads a
// profile, Phase 4's table reads holePlace, and Phase 4 is a page with no 3D in it at all —
// so importing the sampler here put the whole of three.js into the main bundle to answer a
// question about two numbers. The sampling lives in partProfileSample, which only the scene
// that already has a mesh loaded ever imports.
//
// The three (bucketed, measured) pairs, one per opposed pair of faces.
export const PAIRS: [Axis, Axis][] = [['x', 'z'], ['y', 'z'], ['x', 'y']];

// HOW FINELY. Thirty-two is about 12 mm on a 400 mm rail, which is under the width of any
// bore this app cuts and well over the resolution anything answers a hole's place to. The
// cost is the store: three pairs of thirty-two bands is under 1 kB of JSON per part, which
// is a fact worth keeping beside a size record several people load.
export const BUCKETS = 32;

export const pairKey = (a: Axis, b: Axis) => `${a}${b}`;

// Which world axis a face word points along, and whether it points up it. Both come off the
// one table that names the six directions (faces.ts), so nothing here has a second opinion
// about where "back" is.
export const axisOf = (face: Face): Axis => (['x', 'y', 'z'] as const)[
  FACE_DIR[face].findIndex((c) => c !== 0)
];

export const axisUp = (face: Face): boolean => FACE_DIR[face].some((c) => c > 0);

// WHICH BUCKET A FRACTION FALLS IN. Exported for the sampler, which lives in a file of its
// own — see partProfileSample, and the note on PAIRS above for why the split is not cosmetic.
export const bucketOf = (frac: number, buckets = BUCKETS) =>
  Math.max(0, Math.min(buckets - 1, Math.floor(frac * buckets)));

// WHERE THE MATERIAL RUNS ALONG `b`, at the point `frac` of the way along `a` — both as
// fractions of the box, both measured from the box's minimum on that axis.
//
// Null where the profile has never been taken, where this pair is not one of the three, or
// where that bucket holds no triangle at all. Every one of those is the same answer to the
// caller: nothing here is better than the box, so use the box.
export function sectionAt(
  profile: PartProfile | null | undefined,
  a: Axis,
  b: Axis,
  frac: number,
): [number, number] | null {
  const row = profile?.bands?.[pairKey(a, b)];
  if (!row?.length) return null;
  const at = Math.max(0, Math.min(row.length - 1, Math.floor(frac * row.length)));
  return row[at];
}
